import styles from "./line-result.module.scss";
import { useContext, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AppAction, AppContext } from "helpers/context";
import { MapContext } from "helpers/map";
import { ReactComponent as IconTable } from "assets/icon_table_th.svg";
import TryAgain from "components/tryAgain/tryAgain";
import { Paper, LinearProgress } from "@mui/material";
import DistanceChart from "components/results/chart-modal/distance-chart/distance-chart"
import BarChart from "components/results/chart-modal/bar-chart/bar-chart"
import { handleGetLineResultGroupData } from "helpers/groupResultData";
import { handleGetBarChartGroupData } from "helpers/groupBarChartData"
import CustomAccordion from "components/accordion/Accordion";
import ShareTable from "components/results/table-modal/share-table/share-table"
import ExtremeTable from "components/results/table-modal/extreme-table/extreme-table"


function LineResults() {
    const [t] = useTranslation();
    const [context, dispatch] = useContext(AppContext);
    const { AMap } = useContext(MapContext);
    const [chartOption, setChartOption] = useState<any>(null);
    const [distanceChartOption, setDistanceChartOption] = useState<any>([]);
    const [chartHeight, setChartHeight] = useState(0);
    const [activeLegend, setActiveLegend] = useState(0);
    const [defendeActiveLegend, setDefendeActiveLegend] = useState(0);
    const [distanceUnit, setDistanceUnit] = useState<"m" | "km">("km");
    const [distanceMax, setDistanceMax] = useState<number>(0);
    const [tableData, setTableData] = useState<any[]>([]);
    const [defendeTableData, setDefendeTableData] = useState<any[]>([]);
    const [noData, setNoData] = useState<any[]>([]);
    const extremeMapRef = useRef<any>(undefined);

    const handleRetry = () => {
        dispatch({ type: AppAction.setTryAgainType, payload: "lineAnalyse" });
    }

    const handleLegendClick = (index: number, layerId: string) => {
        setActiveLegend(index);
        let i = defendeTableData.findIndex((i) => i[0].layerId === layerId);
        setDefendeActiveLegend(i);
    };

    const handleDistance = () => {
        const shapeObj =
            (context.currentShape as any)?.aMapRef || (context.currentShape as any);
        if (!shapeObj?.getPath) {
            return {
                totalDistance: 0,
                unit: "m" as const
            };
        }
        let path = shapeObj.getPath();

        if (!AMap?.GeometryUtil || path.length < 2) {
            return {
                totalDistance: 0,
                unit: "m" as const
            };
        }

        let totalDistance = 0;
        for (let i = 0; i < path.length - 1; i++) {
            totalDistance += AMap.GeometryUtil.distance(path[i], path[i + 1]);
        }

        return {
            totalDistance,
            unit: totalDistance < 1000 ? ("m" as const) : ("km" as const)
        };
    };

    const handleSetOptions = (
        tableData: any,
        activIndex = activeLegend,
        distanceConfig?: {
            unit: "m" | "km";
            max: number;
        }
    ) => {
        const currentDistanceConfig = distanceConfig || {
            unit: distanceUnit,
            max: distanceMax,
        };

        const { Ynum, option } = handleGetBarChartGroupData({
            tableData,
            activIndex,
            analysedLegends: context.lineAnalysisLegends,
            analysedLocationSet: {
                aggregationUnit: "numberOfLocations"
            },
            distanceAxisConfig: {
                enabled: true,
                unit: currentDistanceConfig.unit,
                max: currentDistanceConfig.max,
            },
            t,
        })

        setChartHeight(Ynum);
        setChartOption(option);
    };

    const handleGetTableData = () => {
        if (context.lineAnalysisResult
            && context.lineAnalysisLegends
            && context.lineAnalysisStatus !== "error"
            && context.lineAnalysisStatus !== "running"
        ) {
            const {
                result,
                fathomDefendeResult,
                noData,
                chartData,
                extremeMap,
                distanceData
            } = handleGetLineResultGroupData({
                resultData: context.lineAnalysisResult,
                legendData: context.lineAnalysisLegends
            })
            const { unit, totalDistance } = handleDistance();
            const max = unit === "km" ? totalDistance / 1000 : totalDistance;

            setDistanceUnit(unit);
            setDistanceMax(max);
            handleSetOptions(chartData, activeLegend, {
                unit,
                max,
            });

            setDistanceChartOption(distanceData);
            setNoData(noData);
            setTableData(result);

            setDefendeTableData(fathomDefendeResult);
            extremeMapRef.current = extremeMap;
        } else {
            setNoData([])
            setDistanceUnit("km");
            setDistanceMax(1);
            handleSetOptions([], 0, {
                unit: "km",
                max: 1,
            });
            setTableData([]);
            setDefendeTableData([]);
            setActiveLegend(0);
            setDefendeActiveLegend(0);
        }
    }

    useEffect(() => {
        handleGetTableData()
    }, [context.lineAnalysisResult, context.lineAnalysisLegends, context.currentShape, AMap]);

    if (context.lineAnalysisStatus && context.lineAnalysisStatus === "error") {
        return (<TryAgain handleRetry={handleRetry} />);
    }

    if (context.lineAnalysisStatus && context.lineAnalysisStatus === "running") {
        return (
            <div className="h-full flex flex-col items-center justify-center">
                <LinearProgress
                    color="success"
                    style={{ width: "40%", marginBottom: "10px" }}
                />
                <div>{t("drawShape.alalyseLoadingContent")}</div>
            </div>
        );
    }
    const randerDetail = () => {
        const layerId = tableData[activeLegend]?.[0]?.layerId
        if (layerId === 'FL_Fluvial_Global_Fathom' ||
            layerId === 'FL_Coastal_Global_Fathom'
        ) {
            return <>
                <CustomAccordion title={t("fathom.Undefended")}>
                    <ShareTable
                        tableData={tableData[activeLegend]}
                        activeLegend={activeLegend}
                        analysedLegends={context.lineAnalysisLegends}
                        isLineAnalysis={true}
                        distanceUnit={distanceUnit}
                        distanceMax={distanceMax}
                        type="line-Undefended"
                    />
                </CustomAccordion>
                <CustomAccordion title={t("fathom.Defended")}>
                    <ShareTable
                        tableData={defendeTableData[defendeActiveLegend]}
                        activeLegend={defendeActiveLegend}
                        analysedLegends={context.lineAnalysisLegends}
                        isLineAnalysis={true}
                        distanceUnit={distanceUnit}
                        distanceMax={distanceMax}
                        type="line-Defended"
                    />
                </CustomAccordion>
            </>
        }
        return <ShareTable
            tableData={tableData[activeLegend]}
            activeLegend={activeLegend}
            analysedLegends={context.lineAnalysisLegends}
            isLineAnalysis={true}
            distanceUnit={distanceUnit}
            distanceMax={distanceMax}
            type="line"
        />
    }

    return <>
        <DistanceChart
            chartData={distanceChartOption}
            noData={noData}
            distanceUnit={distanceUnit}
            distanceMax={distanceMax}
        />
        <BarChart
            tableData={tableData}
            activeLegend={activeLegend}
            noData={noData}
            distanceUnit={distanceUnit}
            chartHeight={chartHeight}
            chartOption={chartOption}
            analysedLegends={context.lineAnalysisLegends}
            handleLegendClick={handleLegendClick}
        />
        {(tableData[activeLegend] || noData.length > 0) && (
            <div >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingRight: "10px" }}>
                    <p className="m-2 font-bold">{t(`layerId.${tableData[activeLegend] ?
                        tableData[activeLegend][0].layerId
                        :
                        (noData[0] ?
                            noData[0].layerId
                            :
                            context.lineAnalysisLegends && context.lineAnalysisLegends[activeLegend].layerId)}`)}</p>
                    <div style={{ padding: "6px 10px", borderRadius: "5px", backgroundColor: "var(--green-hover)", boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.15)" }}>
                        <IconTable />
                    </div>
                </div>
                <Paper
                    sx={{ width: "100%", boxShadow: "none" }}
                    className={styles.table}
                >
                    {randerDetail()}
                </Paper>
            </div>)}
        {extremeMapRef.current && <ExtremeTable
            layerId={tableData[activeLegend] ? tableData[activeLegend][0].layerId : (noData[0] ? noData[0].layerId : context.lineAnalysisLegends && context.lineAnalysisLegends[activeLegend].layerId)}
            extremeMap={extremeMapRef.current}
            distanceUnit={distanceUnit}
        />}
    </>
}

export default LineResults